import React, { useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Bot, ImagePlus, Mail, Save, Send, Sparkles, X } from 'lucide-react';
import { toast } from 'sonner';

const token = () => localStorage.getItem('token') || localStorage.getItem('secours73_token') || '';
const callApi = async (path, options = {}) => {
  const response = await fetch(`/api/index.php?route=${encodeURIComponent(path)}&token=${encodeURIComponent(token())}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || 'Erreur du serveur');
  return data;
};

export default function AssistantIA() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [working, setWorking] = useState(false);
  const [aiStatus, setAiStatus] = useState('');
  const [instruction, setInstruction] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [gmail, setGmail] = useState({ email: '', sender_name: 'Formation Secours 73', app_password: '', configured: false });
  const [gemini, setGemini] = useState({ model: 'gemini-3.7-flash', api_key: '', configured: false });

  useEffect(() => {
    callApi('admin/integrations').then((data) => {
      setGmail((value) => ({ ...value, ...(data.gmail || {}) }));
      setGemini((value) => ({ ...value, ...(data.gemini || {}) }));
    }).catch((error) => toast.error(error.message)).finally(() => setLoading(false));
  }, []);

  const saveConnections = async () => {
    setSaving(true);
    try {
      await callApi('admin/integrations', { method: 'PUT', body: JSON.stringify({ gmail, gemini }) });
      toast.success('Connexions enregistrées');
      setGmail((value) => ({ ...value, app_password: '', configured: value.configured || Boolean(value.app_password) }));
      setGemini((value) => ({ ...value, api_key: '', configured: value.configured || Boolean(value.api_key) }));
    } catch (error) { toast.error(error.message); } finally { setSaving(false); }
  };

  const testGmail = async () => {
    try {
      await saveConnections();
      const data = await callApi('admin/integrations/test-gmail', { method: 'POST', body: JSON.stringify({ to: gmail.email }) });
      toast.success(data.message);
    } catch (error) { toast.error(error.message); }
  };

  const askGemini = async () => {
    if (!instruction.trim()) return toast.error('Décrivez la modification souhaitée');
    setWorking(true); setAiStatus('Gemini prépare et publie la modification…');
    try {
      await saveConnections();
      const requestBody = { instruction, image: screenshot ? { mime_type: screenshot.mimeType, data: screenshot.data } : null };
      let proposal;
      try {
        const applied = await callApi('admin/ai/apply', { method: 'POST', body: JSON.stringify(requestBody) });
        if (applied.applied) proposal = applied.customization || true;
      } catch (directError) {
        // Compatibilité avec les installations utilisant encore l'ancienne route.
      }
      if (!proposal) {
        const suggested = await callApi('admin/ai/suggest', { method: 'POST', body: JSON.stringify(requestBody) });
        proposal = suggested.proposal || suggested.customization || suggested.data;
        if (!proposal || typeof proposal !== 'object') throw new Error(suggested.detail || 'Réponse Gemini reçue, mais sans données de modification.');
        await callApi('admin/customization', { method: 'PUT', body: JSON.stringify(proposal) });
      }
      setInstruction('');
      setScreenshot(null);
      setAiStatus('Modification publiée avec succès.');
      toast.success('Modification appliquée directement sur le site');
      window.setTimeout(() => window.open(`/?mise-a-jour=${Date.now()}`, '_blank'), 400);
    } catch (error) {
      const message = error.message || 'Assistant IA indisponible';
      setAiStatus(`Échec : ${message}`);
      toast.error(message);
    } finally { setWorking(false); }
  };

  const selectScreenshot = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) return toast.error('Utilisez une image JPG, PNG ou WEBP.');
    if (file.size > 4 * 1024 * 1024) return toast.error('La capture ne doit pas dépasser 4 Mo.');
    const reader = new FileReader();
    reader.onload = () => {
      const value = String(reader.result || '');
      setScreenshot({ name: file.name, mimeType: file.type, data: value.split(',')[1] || '', preview: value });
      setAiStatus('Capture ajoutée. Décrivez précisément ce qu’il faut modifier.');
    };
    reader.onerror = () => toast.error('Impossible de lire cette image.');
    reader.readAsDataURL(file);
  };

  if (loading) return <Layout><div className="p-10 text-center">Chargement…</div></Layout>;
  return <Layout><div className="max-w-5xl mx-auto px-4 py-8 space-y-8">
    <div><h1 className="text-3xl font-bold flex items-center gap-3"><Sparkles className="text-purple-600"/> Assistant IA et Gmail</h1><p className="text-slate-600 mt-2">Personnalisez le site avec Gemini et configurez les e-mails automatiques.</p></div>
    <Card><CardHeader><CardTitle className="flex gap-2"><Bot/> Assistant de personnalisation Gemini</CardTitle></CardHeader><CardContent className="space-y-4">
      <div><Label>Clé API Gemini</Label><Input type="password" value={gemini.api_key} onChange={(e)=>setGemini({...gemini,api_key:e.target.value})} placeholder={gemini.configured?'Clé enregistrée — laissez vide pour la conserver':'Collez la clé créée dans Google AI Studio'}/></div>
      <div><Label>Modèle Gemini</Label><Input value={gemini.model} onChange={(e)=>setGemini({...gemini,model:e.target.value})}/></div>
      <div><Label>Modification souhaitée</Label><Textarea rows={6} value={instruction} onChange={(e)=>setInstruction(e.target.value)} placeholder="Exemple : sur la capture, remplace ce titre, agrandis le texte et présente cette partie sous forme de cartes."/><p className="text-xs text-slate-500 mt-2">Gemini peut remplacer les textes visibles sur toutes les pages et adapter leur présentation, leurs couleurs, espacements et dimensions.</p></div>
      <div className="space-y-3">
        <Label>Photo ou capture du site (facultatif)</Label>
        {!screenshot ? <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-purple-200 bg-purple-50 px-4 py-6 text-sm font-medium text-purple-700 hover:bg-purple-100">
          <ImagePlus className="h-5 w-5"/> Ajouter une photo ou une capture
          <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={selectScreenshot}/>
        </label> : <div className="relative overflow-hidden rounded-lg border bg-slate-50 p-3">
          <img src={screenshot.preview} alt="Capture à analyser" className="max-h-72 w-full rounded object-contain"/>
          <button type="button" onClick={()=>setScreenshot(null)} className="absolute right-5 top-5 rounded-full bg-white p-2 text-slate-700 shadow" aria-label="Retirer la capture"><X className="h-4 w-4"/></button>
          <p className="mt-2 truncate text-xs text-slate-500">{screenshot.name}</p>
        </div>}
        <p className="text-xs text-slate-500">JPG, PNG ou WEBP, 4 Mo maximum. Vous pouvez entourer la zone à modifier sur la capture.</p>
      </div>
      <Button onClick={askGemini} disabled={working} className="bg-purple-600 hover:bg-purple-700"><Sparkles className="w-4 h-4 mr-2"/>{working?'Gemini applique la modification…':'Appliquer la modification avec l’IA'}</Button>
      {aiStatus && <div className={`rounded-lg border p-3 text-sm ${aiStatus.startsWith('Échec')?'border-red-200 bg-red-50 text-red-800':'border-emerald-200 bg-emerald-50 text-emerald-800'}`}>{aiStatus}</div>}
    </CardContent></Card>
    <Card><CardHeader><CardTitle className="flex gap-2"><Mail/> Connexion Gmail</CardTitle></CardHeader><CardContent className="space-y-4">
      <p className="text-sm text-slate-600">Utilisez un mot de passe d’application Google, jamais le mot de passe habituel du compte Gmail.</p>
      <div><Label>Adresse Gmail d’envoi</Label><Input type="email" value={gmail.email} onChange={(e)=>setGmail({...gmail,email:e.target.value})} placeholder="formation@secours73.fr"/></div>
      <div><Label>Nom d’expéditeur</Label><Input value={gmail.sender_name} onChange={(e)=>setGmail({...gmail,sender_name:e.target.value})}/></div>
      <div><Label>Mot de passe d’application Gmail</Label><Input type="password" value={gmail.app_password} onChange={(e)=>setGmail({...gmail,app_password:e.target.value})} placeholder={gmail.configured?'Connexion enregistrée — laissez vide pour la conserver':'Code Google à 16 caractères'}/></div>
      <div className="flex gap-3"><Button onClick={saveConnections} disabled={saving}><Save className="w-4 h-4 mr-2"/>Enregistrer</Button><Button variant="outline" onClick={testGmail}><Send className="w-4 h-4 mr-2"/>Envoyer un test</Button></div>
    </CardContent></Card>
  </div></Layout>;
}
